import { useMemo, useState } from 'react'
import { Input, Form, message, Select, Space } from 'antd'
import { EyeOutlined, SendOutlined } from '@ant-design/icons'
import { HiOutlinePencil } from 'react-icons/hi'
import { Button } from '@/components/ui'
import { router } from '@inertiajs/react'
import { api } from '@/lib/apiClient'
import SendTestEmailModal from './SendTestEmailModal'
import EmailPreviewModal from './EmailPreviewModal'
import EmailEditorModal from './EmailEditorModal'
import LoadEmailContentModal, { type ImportedEmailContent } from './LoadEmailContentModal'
import {
  type Event,
  type AutomatedEmail,
  type AutomatedEmailAudience,
  AUTOMATED_EMAIL_AUDIENCE_OPTIONS,
  DEFAULT_AUTOMATED_EMAIL_AUDIENCE,
  defaultEmailDesignJson,
  getAutomatedEmailTitle,
  isAudienceSelectable,
} from '../types'
import { type MergeTagOptions } from '@/lib/mergeTags'
import FixedBottomActions from '@/components/layouts/events/FixedBottomActions'
import '@/styles/compact-form.css'

/**
 * Where the form returns to when the user backs out: the Automated Email tab of
 * the Email Blast index. Exported because the page chrome's back arrow has to
 * land in the same place as the Cancel button, and the two live in different
 * files once the form is hosted rather than owned by the page.
 */
export const automatedEmailIndexPath = (eventSlug: string) =>
  `/events/${eventSlug}/messaging/email_blasts?tab=automated`

export type AutomatedEmailFormProps = {
  event: Event
  automatedEmail: AutomatedEmail
  mergeTagOptions?: MergeTagOptions
  /**
   * Sent along with the save as `return_to`, naming which page the server should
   * redirect back to. The only accepted value mirrors
   * `EmailBlastsController::RETURN_TO_CERTIFICATES`; the server matches it
   * against that allowlist and builds the path itself, so nothing here is
   * interpolated into a redirect. Omit it to get the default redirect.
   */
  returnTo?: 'certificates'
  /**
   * How the action bar is placed. `fixed` pins it to the bottom of the viewport
   * and is what a full page wants; `inline` leaves it in the document flow, for
   * hosts that render this form inside a tab rather than as the whole screen.
   * @default 'fixed'
   */
  renderActions?: 'fixed' | 'inline'
  /**
   * What Cancel does. Defaults to returning to the Automated Email tab, which is
   * where the standalone page came from.
   */
  onCancel?: () => void
}

type PreviewRecipient = {
  id?: string
  name: string
  email: string
  source?: 'selected' | 'recipient' | 'latest' | 'sample'
}

type PreviewResponse = {
  html: string
  subject: string
  attendee?: PreviewRecipient
}

type SendTestResponse = {
  sent_count: number
  attendee?: PreviewRecipient
}

type AutomatedEmailFormValues = {
  subjectLine: string
  senderEmail: string
  senderEmailDomain: string
  replyToEmail: string
  senderName: string
  /** Only registered on the form for templates that offer a choice of audience. */
  audience?: AutomatedEmailAudience
}

/** Values the preview modal may override before the form is saved. */
type PreviewOverrides = Partial<Pick<AutomatedEmailFormValues, 'subjectLine'>> & {
  htmlContent?: string
}

/**
 * The automated email editor: sender/subject/audience fields on the left, the
 * Unlayer body and its modals on the right, and the save/preview/test actions
 * along the bottom.
 */
const AutomatedEmailForm = ({
  event,
  automatedEmail,
  mergeTagOptions,
  returnTo,
  renderActions = 'fixed',
  onCancel,
}: AutomatedEmailFormProps) => {
  const [form] = Form.useForm<AutomatedEmailFormValues>()

  const pageTitle = getAutomatedEmailTitle(automatedEmail)
  const showAudienceControl = isAudienceSelectable(automatedEmail?.templateType)
  const currentAudience = automatedEmail?.audience || DEFAULT_AUTOMATED_EMAIL_AUDIENCE

  const EMAIL_DOMAINS = [{ label: '@avaeventhub.com', value: '@avaeventhub.com' }]

  const initialSenderParts = useMemo(() => {
    const full = automatedEmail?.senderEmail || ''
    const [local, domain] = full.split('@', 2)
    const candidateDomain = domain ? `@${domain}` : EMAIL_DOMAINS[0].value
    const resolvedDomain = EMAIL_DOMAINS.some((d) => d.value === candidateDomain)
      ? candidateDomain
      : EMAIL_DOMAINS[0].value
    return {
      local: local || '',
      domain: resolvedDomain,
    }
  }, [automatedEmail?.senderEmail])

  const initialHtml = useMemo(
    () => automatedEmail?.htmlContent || '',
    [automatedEmail?.htmlContent]
  )

  // Form state
  const [htmlContent, setHtmlContent] = useState(initialHtml)
  const [designJson, setDesignJson] = useState<Record<string, unknown>>(
    automatedEmail?.designJson || defaultEmailDesignJson
  )

  // Modal states
  const [testEmailModalOpen, setTestEmailModalOpen] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [editorModalOpen, setEditorModalOpen] = useState(false)
  const [loadContentModalOpen, setLoadContentModalOpen] = useState(false)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [previewSubject, setPreviewSubject] = useState<string | null>(null)
  const [previewRecipient, setPreviewRecipient] = useState<PreviewRecipient | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [testSendLoading, setTestSendLoading] = useState(false)

  const handleEditEmail = () => {
    setEditorModalOpen(true)
  }

  const handleSaveEmailContent = (newHtml: string, newDesignJson: Record<string, unknown>) => {
    setHtmlContent(newHtml)
    setDesignJson(newDesignJson)
    setEditorModalOpen(false)
    message.success('Email content saved')
  }

  const handleLoadContentFromEvent = (content: ImportedEmailContent) => {
    const nextSubjectLine = content.subjectLine || ''
    const nextHtmlContent = content.htmlContent || ''
    const nextDesignJson = content.designJson || defaultEmailDesignJson

    form.setFieldValue('subjectLine', nextSubjectLine)
    setHtmlContent(nextHtmlContent)
    setDesignJson(nextDesignJson)
    message.success(`Loaded content from "${content.sourceEventName}"`)
  }

  const handlePreview = async () => {
    if (!htmlContent || !htmlContent.trim()) {
      message.error('Email content cannot be empty')
      return
    }

    const values = form.getFieldsValue()
    setPreviewLoading(true)
    try {
      const response = await api.post<PreviewResponse>(
        `/events/${event.slug}/messaging/email_blasts/preview`,
        {
          subject_line: values.subjectLine || automatedEmail?.subjectLine || '',
          html_content: htmlContent,
        }
      )

      setPreviewHtml(response.html || htmlContent)
      setPreviewSubject(response.subject || values.subjectLine || automatedEmail?.subjectLine || '')
      setPreviewRecipient(response.attendee || null)
      setPreviewModalOpen(true)
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to load preview.')
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleSendTest = () => {
    setTestEmailModalOpen(true)
  }

  const handleSendTestEmails = async (emails: string[]) => {
    if (!htmlContent || !htmlContent.trim()) {
      message.error('Email content cannot be empty')
      return
    }

    const values = form.getFieldsValue()
    const senderEmailFull = `${values.senderEmail || ''}${values.senderEmailDomain || ''}`.trim()

    if (!(values.subjectLine || automatedEmail?.subjectLine)?.trim()) {
      message.error('Please enter subject line')
      return
    }

    if (!senderEmailFull) {
      message.error('Please enter sender email')
      return
    }

    if (!values.senderName?.trim() && !automatedEmail?.senderName) {
      message.error('Please enter sender name')
      return
    }

    setTestSendLoading(true)
    try {
      const response = await api.post<SendTestResponse>(
        `/events/${event.slug}/messaging/email_blasts/send_test`,
        {
          test_emails: emails,
          subject_line: values.subjectLine || automatedEmail?.subjectLine || '',
          html_content: htmlContent,
          sender_email: senderEmailFull,
          sender_name: values.senderName || automatedEmail?.senderName || '',
          reply_to_email: values.replyToEmail || automatedEmail?.replyToEmail || '',
        }
      )

      const count = response.sent_count ?? emails.length
      const attendee = response.attendee
      const suffix = attendee?.source === 'sample' ? ' (using sample attendee data)' : ''
      message.success(
        `Test email has been sent to ${count} recipient${count !== 1 ? 's' : ''}.${suffix}`
      )
      setTestEmailModalOpen(false)
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to send test email.')
    } finally {
      setTestSendLoading(false)
    }
  }

  const handleSubmit = (values: AutomatedEmailFormValues) => {
    const payload = {
      automated_email_template: {
        template_name: automatedEmail.templateName,
        subject_line: values.subjectLine,
        sender_email: `${values.senderEmail || ''}${values.senderEmailDomain || ''}`,
        sender_name: values.senderName,
        reply_to_email: values.replyToEmail,
        html_content: htmlContent,
        design_json: designJson,
        enabled: automatedEmail.enabled,
        // Only some template types render an audience control; every other one
        // just round-trips its stored value.
        audience: values.audience || currentAudience,
      },
    }

    // Always the same endpoint. returnTo only adds a query parameter that tells
    // the server where to send the browser afterwards — it never redirects the
    // save itself somewhere else.
    const updatePath = `/events/${event?.slug}/messaging/email_blasts/automated/${automatedEmail.id}`

    router.patch(
      returnTo ? `${updatePath}?return_to=${encodeURIComponent(returnTo)}` : updatePath,
      payload,
      {
        preserveScroll: true,
        onError: () => message.error('Failed to update automated email.'),
      }
    )
  }

  const handleCancel =
    onCancel ??
    (() => {
      router.visit(automatedEmailIndexPath(event?.slug))
    })

  const getFormValues = (overrides: PreviewOverrides = {}) => {
    const values = form.getFieldsValue()
    return {
      campaignName: pageTitle,
      subjectLine: overrides.subjectLine || values.subjectLine || automatedEmail?.subjectLine || '',
      senderName: values.senderName || automatedEmail?.senderName || '',
      senderEmail: `${values.senderEmail || initialSenderParts.local}${
        values.senderEmailDomain || initialSenderParts.domain
      }`,
      recipientCount: 0,
      htmlContent: overrides.htmlContent || htmlContent,
      updatedAt: new Date().toISOString(),
    }
  }

  const leftActions = (
    <Space>
      <Button variant="primary" onClick={() => form.submit()}>
        Save as Draft
      </Button>
      <Button variant="secondary" icon={<EyeOutlined />} onClick={handlePreview}>
        Preview
      </Button>
      <Button variant="secondary" icon={<SendOutlined />} onClick={handleSendTest}>
        Send Test Email
      </Button>
    </Space>
  )

  const rightActions = (
    <Button variant="tertiary" onClick={handleCancel}>
      Cancel
    </Button>
  )

  return (
    <>
      <div className="flex gap-8 px-14 py-6">
        {/* Left Panel - Form */}
        <div className="w-[400px] flex-shrink-0">
          <Form<AutomatedEmailFormValues>
            size="small"
            form={form}
            layout="vertical"
            className="compact-form"
            initialValues={{
              subjectLine: automatedEmail?.subjectLine || '',
              senderEmail: initialSenderParts.local,
              senderEmailDomain: initialSenderParts.domain,
              senderName: automatedEmail?.senderName || '',
              replyToEmail: automatedEmail?.replyToEmail || '',
              audience: currentAudience,
            }}
            onFinish={handleSubmit}
          >
            {showAudienceControl && (
              <Form.Item
                label="Send to"
                name="audience"
                extra="Attended only sends to registrants who checked in to the event."
              >
                <Select size="small" options={AUTOMATED_EMAIL_AUDIENCE_OPTIONS} />
              </Form.Item>
            )}

            <Form.Item
              label="Email Subject Line"
              name="subjectLine"
              rules={[{ required: true, message: 'Please enter subject line' }]}
            >
              <Input placeholder="Enter subject line" size="small" />
            </Form.Item>

            <Form.Item label="Sender Email" required>
              <Space.Compact className="w-full">
                <Form.Item
                  name="senderEmail"
                  noStyle
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input style={{ width: '40%' }} placeholder="Enter email" size="small" />
                </Form.Item>
                <Form.Item name="senderEmailDomain" noStyle>
                  <Select style={{ width: '60%' }} size="small" options={EMAIL_DOMAINS} />
                </Form.Item>
              </Space.Compact>
            </Form.Item>

            <Form.Item
              label="Sender Name"
              name="senderName"
              rules={[{ required: true, message: 'Please enter sender name' }]}
            >
              <Input placeholder="Enter sender name" size="small" />
            </Form.Item>

            <Form.Item label="Reply-to-Email" name="replyToEmail">
              <Input placeholder="Enter reply-to-email" size="small" />
            </Form.Item>
          </Form>
        </div>

        {/* Right Panel - Email Preview */}
        <div className="flex-1 bg-gray-50 rounded-lg p-6">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium m-0">Email Editor</h3>
              <Space>
                <Button variant="secondary" onClick={() => setLoadContentModalOpen(true)}>
                  Load From Event
                </Button>
                <Button variant="primary" icon={<HiOutlinePencil />} onClick={handleEditEmail}>
                  Edit
                </Button>
              </Space>
            </div>
          </div>

          {/* Email Preview */}
          <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
            <iframe
              srcDoc={htmlContent}
              title="Email Preview"
              className="w-full h-[500px] border-0"
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      </div>

      {renderActions === 'inline' ? (
        // Deliberately not FixedBottomActions: that bar is positioned against the
        // viewport with a hardcoded sidebar offset, which only lines up when the
        // form is the whole page. In a tab it has to sit in the flow instead.
        <div className="mx-14 mb-6 flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4">
          <div>{leftActions}</div>
          <div>{rightActions}</div>
        </div>
      ) : (
        <FixedBottomActions leftActions={leftActions} rightActions={rightActions} />
      )}

      {/* Send Test Email Modal */}
      <SendTestEmailModal
        open={testEmailModalOpen}
        onClose={() => setTestEmailModalOpen(false)}
        onSend={handleSendTestEmails}
        loading={testSendLoading}
      />

      {/* Email Preview Modal */}
      <EmailPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        email={getFormValues({
          htmlContent: previewHtml || htmlContent,
          subjectLine:
            previewSubject ||
            form.getFieldValue('subjectLine') ||
            automatedEmail?.subjectLine ||
            '',
        })}
        mode="view"
        previewRecipient={previewRecipient || undefined}
        loading={previewLoading}
      />

      {/* Email Editor Modal */}
      <EmailEditorModal
        open={editorModalOpen}
        onClose={() => setEditorModalOpen(false)}
        onSave={handleSaveEmailContent}
        initialHtml={htmlContent}
        initialDesignJson={designJson}
        mergeTagOptions={mergeTagOptions}
      />

      <LoadEmailContentModal
        open={loadContentModalOpen}
        eventSlug={event.slug}
        onClose={() => setLoadContentModalOpen(false)}
        onApply={handleLoadContentFromEvent}
      />
    </>
  )
}

export default AutomatedEmailForm
